import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export type HotMemoryItemType =
  | "emailThread"
  | "emailMessage"
  | "attachment"
  | "dbRetailer"
  | "dbPO"
  | "dbSupplier"
  | "resourceFile";

export interface HotMemoryItem {
  id: string;
  type: HotMemoryItemType;
  label?: string;
  data?: Record<string, any>;
  pinned?: boolean;
  addedAt: number;
}

export interface HotMemoryState {
  itemsByUser: Record<string, HotMemoryItem[]>;
}

const initialState: HotMemoryState = {
  itemsByUser: {},
};

const MAX_ITEMS = 40;

const hotMemorySlice = createSlice({
  name: "hotMemory",
  initialState,
  reducers: {
    addHotMemoryItem: (
      state,
      action: PayloadAction<{ userId: string; item: Omit<HotMemoryItem, "addedAt"> }>
    ) => {
      const userId = action.payload.userId;
      if (!userId) return;

      const now = Date.now();
      const nextItem: HotMemoryItem = {
        ...action.payload.item,
        addedAt: now,
      };

      const current = state.itemsByUser[userId] || [];
      const deduped = current.filter(
        (it) => !(it.type === nextItem.type && it.id === nextItem.id)
      );
      const merged = [nextItem, ...deduped];
      const pinned = merged.filter((it) => it.pinned);
      const rest = merged.filter((it) => !it.pinned);
      state.itemsByUser[userId] = [...pinned, ...rest].slice(0, MAX_ITEMS);
    },
    removeHotMemoryItem: (
      state,
      action: PayloadAction<{ userId: string; id: string; type: HotMemoryItemType }>
    ) => {
      const userId = action.payload.userId;
      if (!userId) return;
      const current = state.itemsByUser[userId] || [];
      state.itemsByUser[userId] = current.filter(
        (it) => !(it.type === action.payload.type && it.id === action.payload.id)
      );
    },
    clearHotMemory: (state, action: PayloadAction<{ userId: string }>) => {
      const userId = action.payload.userId;
      if (!userId) return;
      state.itemsByUser[userId] = [];
    },
    toggleHotMemoryPin: (
      state,
      action: PayloadAction<{ userId: string; id: string; type: HotMemoryItemType }>
    ) => {
      const userId = action.payload.userId;
      if (!userId) return;
      const current = state.itemsByUser[userId] || [];
      const next = current.map((it) => {
        if (it.type === action.payload.type && it.id === action.payload.id) {
          return { ...it, pinned: !it.pinned };
        }
        return it;
      });
      const pinned = next.filter((it) => it.pinned);
      const rest = next.filter((it) => !it.pinned);
      state.itemsByUser[userId] = [...pinned, ...rest];
    },
  },
});

export const {
  addHotMemoryItem,
  removeHotMemoryItem,
  clearHotMemory,
  toggleHotMemoryPin,
} = hotMemorySlice.actions;
export default hotMemorySlice.reducer;
